import { create } from 'zustand';
import { getUser, signIn, signOut } from '@/services/auth';
import type { BffUser } from '@/services/auth';
import { setAuthErrorHandler } from '@/services/api';

interface AuthStore {
  user: BffUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (returnUrl?: string) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  // Full-page redirect to the BFF login (which drives the OIDC flow server-side).
  login: (returnUrl?: string) => signIn(returnUrl),

  // Full-page redirect to the BFF logout (clears the session + ends the auth-server session).
  logout: () => signOut(),

  // Read auth status from the BFF on app startup / after returning from login.
  checkAuth: async () => {
    set({ isLoading: true });
    const user = await getUser();
    set({
      user,
      isAuthenticated: user?.isAuthenticated === true,
      isLoading: false,
    });
  },
}));

// When an API call ends up unauthorized (after the one refresh-retry in the interceptor),
// clear local state and send the user through the BFF login, back to the current page and its
// query (a /consent/callback?consentId=…&accountId=… must survive the login, or the consent is
// never finalized). Skip the no-permission page so a role denial doesn't loop, and skip while
// already mid-login.
setAuthErrorHandler(() => {
  const path = window.location.pathname;
  if (path === '/no-permission' || path === '/login') return;
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
  signIn();
});
